<template>
  <SoDialog v-model="value" card-title="Submit Reading with Warnings">
    <p v-for="([reading, register], index) in invalidReadings" :key="index">
      The read you have entered
      <span class="font-weight-medium bg-danger"
        >{{ reading }}{{ register.unit }}</span
      >
      does not align with the expected read for {{ formattedDate }}. Based on
      previous readings we are expecting a read between
      <span class="font-weight-medium"
        >{{ register.lowerBound }}{{ register.unit }}</span
      >
      and
      <span class="font-weight-medium"
        >{{ register.upperBound }}{{ register.unit }} </span
      >.
    </p>
    <p class="mt-3">
      For troubleshooting steps go to the
      <a
        class="text-primary"
        href="https://home.so.energy/posts/2119655-how-to-submit-meter-readings-process-explained"
        target="_blank"
        >Submitting Meter Reads - Knowledgebase Article
      </a>
    </p>
    <h5 class="mt-3">Step 1</h5>
    <p>Double check the customer reads</p>
    <blockquote class="blockquote">
      I can see the readings you have provided are a bit different to what we're
      expecting. Can I double check these numbers again with you?
    </blockquote>
    <p>Guide the customer to our help pages:</p>
    <p>
      <a
        class="text-primary"
        href="https://justsoyouknow.so.energy/posts/2110561-how-do-i-read-my-traditional-non-smart-meter-"
        target="_blank"
      >
        How To Read Your Meters
      </a>
    </p>
    <p>
      <a
        class="text-primary"
        href="https://justsoyouknow.so.energy/posts/2110506-how-do-i-read-my-smart-meter-"
        target="_blank"
      >
        How To Read Your Smart Meters
      </a>
    </p>
    <h5 class="mt-3">Step 2 (phone/webchat)</h5>
    <p>
      Ask if the customer can get a reading right now. Guide the customer,
      following the help page.
    </p>
    <blockquote class="blockquote">
      Are you at the property right now? Perhaps we can take a reading together
      while I am on the phone to offer additional guidance.
    </blockquote>
    <h5 class="mt-3">Step 3</h5>
    <p class="mb-5">
      If the read is still out of line, ask the customer to email us a photo of
      the meter with the meter serial number and the reading visible so we can
      double check. Only then, submit the reading.
    </p>
    <SoButton
      class="mr-2"
      title="I am sure this reading is correct"
      type="primary"
      :loading="submissionInProgress"
      :disabled="submissionInProgress"
      large
      @click="$emit('submit')"
    />
    <SoButton title="Back" large @click="value = false" />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import SoDialog from "@/components/SoDialog.vue"
  import { formatDate } from "@/helpers/dateHelper"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      invalidReadings: {
        type: Object as PropType<Array<[number, IRegister]>>,
        required: true,
      },
      readingDate: {
        type: Object as PropType<Date | null>,
        default: null,
      },
      submissionInProgress: {
        type: Boolean,
        required: true,
      },
    },
    emits: ["update:modelValue", "submit"],

    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      formattedDate() {
        return formatDate(dayjs(this.readingDate))
      },
    },
  })
</script>
